import { PaymentAccount } from '../Api';

// #region STORAGE
export interface IFuncUpdater<T> {
  (previousState?: T): T;
}
function isFunction<T>(obj: any): obj is T {
  return typeof obj === 'function';
}
function createStorage(storage: Storage) {
  /**
   * gets and parses data from storage
   * @param key string
   * @param defaultValue any
   */
  function getItem<T = 'string'>(
    key: string,
    defaultValue?: T | IFuncUpdater<T>,
  ) {
    const raw = storage.getItem(key);
    if (raw) {
      try {
        return JSON.parse(raw) as T;
      } catch (error) {
        // clear storage if bad data
        setItem(key, undefined);
      }
    }
    if (isFunction<IFuncUpdater<T>>(defaultValue)) {
      return defaultValue();
    }
    return defaultValue;
  }

  /**
   * saves data to storage, passing undefined will clear storage
   * @param key string
   * @param value any
   */
  function setItem<T>(key: string, value?: T | IFuncUpdater<T>) {
    try {
      if (typeof value === 'undefined') {
        storage.removeItem(key);
      } else if (isFunction<IFuncUpdater<T>>(value)) {
        const previousState = getItem<T>(key);
        const currentState = value(previousState);
        storage.setItem(key, JSON.stringify(currentState));
      } else {
        storage.setItem(key, JSON.stringify(value));
      }
    } catch (error) {}
  }

  /**
   * removes data from storage
   * @param key string
   */
  function removeItem(key: string) {
    storage.removeItem(key);
  }

  /**
   * clears all data in storage
   */
  function clear() {
    storage.clear();
  }

  function getKeys() {
    return Object.keys(storage);
  }

  return { getItem, setItem, clear, removeItem, getKeys };
}

// Fallback storage object
class StorageMap extends Map implements Storage {
  setItem = this.set;

  getItem = this.get;

  removeItem = this.delete;

  clear = this.clear;

  length = this.size;

  key = (index: number) => {
    const key = this.keys()[index];
    return key || null;
  };
}

let sessionStorage = null;
try {
  /** Short term storage. last as long as browser session */
  sessionStorage = window.sessionStorage;
} catch (e) {
  sessionStorage = new StorageMap();
}

let localStorage = null;
try {
  /** Long term storage. last until browser cache cleared */
  localStorage = window.localStorage;
} catch (e) {
  localStorage = new StorageMap();
}

const session = createStorage(sessionStorage);
const storage = createStorage(localStorage);
export { session, storage };
// #endregion

// #region PAYMENTS
const PAYMENTS_SHORT_TERM_STORAGE_KEY = '$fd.wo.pa';

export const getPayments = () =>
  session.getItem<PaymentAccount[]>(PAYMENTS_SHORT_TERM_STORAGE_KEY);

export const setPayments = (data: PaymentAccount[]) =>
  session.setItem(PAYMENTS_SHORT_TERM_STORAGE_KEY, data);

// #endregion

const BASKET_STORAGE_KEY_PREFIX = '$fd.wo.b';
// #region BASKET
export const BASKET_STORAGE_KEY = (menuId: number | string) =>
  `${BASKET_STORAGE_KEY_PREFIX}[${menuId}]`;

type BasketItem = {
  itemId: number;
  options: number[];
  order: number;
  count: number;
};
type Basket = {
  menuId: number;
  items: { [uniqueKey: string]: BasketItem };
  paymentId?: string;
  tip?: number;
  requestedTime?: string;
  chefNote?: string;
  pickupLocation?: number;
  tableServiceCategory?: number;
  tableNumber?: number;
};
export const getBasket = (menuId: number | string) =>
  storage.getItem<Basket>(BASKET_STORAGE_KEY(menuId));

export const setBasket = (menuId: number | string, basket?: Basket) =>
  storage.setItem(BASKET_STORAGE_KEY(menuId), basket);
// #endregion

// #region Submitted order meta
const BASKET_ORDER_SUBMITTED_STORAGE_KEY = (orderId: number | string) =>
  `${BASKET_STORAGE_KEY_PREFIX}.om[${orderId}]`;

type SubmittedOrderMeta = {
  menuId: number;
  paymentId: string;
};
export const getSubmittedOrderMeta = (orderId: number | string) =>
  session.getItem<SubmittedOrderMeta>(
    BASKET_ORDER_SUBMITTED_STORAGE_KEY(orderId),
  );

export const setSubmittedOrderMeta = ({
  orderId,
  ...meta
}: {
  orderId: number;
} & SubmittedOrderMeta) =>
  session.setItem(BASKET_ORDER_SUBMITTED_STORAGE_KEY(orderId), meta);
// #endregion
